import { API_SERVER } from '../config/constant';
import { reportSessionEnd } from '../utils/sessionEvents';
import { serverWidgetFileName } from '../app/ai-generator/generatorUi';
import { parseSseBuffer } from '../app/ai-generator/editChatState';

const headers = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

function throwApiError(data, status) {
  reportSessionEnd(status, data);
  const err = new Error(data.message || 'Request failed');
  err.statusCode = status;
  Object.assign(err, data);
  throw err;
}

/**
 * Start a generation.
 *
 * `options` (all optional — an older backend ignores what it does not know):
 *   style      — visual/equipment style id (flat, hp-hmi, convex, line-art, dark…)
 *   detail     — schematic | simple | medium (equipment styles only)
 *   nVariants  — 1 | 3; omitted lets the backend decide (3 on the icon and
 *                equipment paths, 1 on the old path)
 */
export async function generateWidget(prompt, token, sessionId, creativity, options = {}) {
  const body = { prompt, session_id: sessionId, creativity };
  if (options.style) body.style = options.style;
  if (options.detail) body.detail = options.detail;
  if (options.nVariants === 1 || options.nVariants === 3) body.n_variants = options.nVariants;
  const res = await fetch(`${API_SERVER}/ai-generator/generate`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export async function refineWidget(baseJobId, instruction, token, creativity) {
  // Edit-mode: create a new job derived from an existing one (base_job_id).
  // First 3 completed refines in a widget lineage are free; further ones cost 1 credit.
  // Response shape matches generateWidget — client polls /job/:id and /job/:id/stream.
  const res = await fetch(`${API_SERVER}/ai-generator/refine`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ base_job_id: baseJobId, instruction, creativity }),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export async function vectorizeImage(file, token) {
  // Import-mode: POST a raster (PNG/JPG/WEBP) or legacy SVG as multipart/form-data
  // to the existing converter/vectorize endpoint. Returns the parsed JSON:
  //   { status, fileName, svghmi, stats:{ paths, bytes, heavy, warning, simplified },
  //     creditsRemaining, sourceUrl, charged: 'credit',
  //     trial_generations_remaining: null }
  // Since 2026-10-12 every account pays 1 credit (sign-up credits too); the
  // trial field stays null for compatibility. See AiGenerator.js's
  // onConvertImport.
  // NOTE: do NOT set Content-Type here — the browser adds the multipart boundary.
  const form = new FormData();
  form.append('file', file, file.name);

  const res = await fetch(`${API_SERVER}/converter/vectorize`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  let data = null;
  try { data = await res.json(); } catch { data = null; }

  if (!res.ok) {
    // The endpoint returns errors as { status:false, msg } (or, for an account
    // blocked until a purchase, { success:false, error:'PURCHASE_REQUIRED',
    // message }). Surface the
    // distinct failure modes with readable text; res.status 403 only ever
    // means "buy credits to keep using this" here — never an auth failure —
    // see onConvertImport, which only treats 401 as a sign-in redirect.
    const fallback = {
      400: 'Unsupported or invalid file. Upload a PNG, JPG, WEBP or SVG under 9 MB.',
      401: 'Please sign in to import a widget.',
      402: 'Not enough credits. Importing a widget costs 1 credit.',
      403: 'AI generation on this account is unlocked by a credit purchase.',
      502: 'The image vectorizer is temporarily unavailable. Please try again in a moment.',
    }[res.status] || 'Failed to convert the image. Please try again.';
    const err = new Error((data && (data.msg || data.message)) || fallback);
    err.statusCode = res.status;
    if (data) Object.assign(err, data);
    throw err;
  }

  return data;
}

export async function renderSvghmiContent(svghmi, mockValues, token) {
  // Import-mode (vectorize) property controls: stateless re-render of an SVGHMI string
  // with different param values. Unlike renderAiWidget (job-id based), this endpoint
  // takes the SVGHMI source directly since imported widgets aren't tied to a job.
  const res = await fetch(`${API_SERVER}/converter/render-svghmi`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ svghmi, mockValues }),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export async function getJobStatus(jobId, token) {
  const res = await fetch(`${API_SERVER}/ai-generator/job/${jobId}`, {
    headers: headers(token),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export function createJobSSE(jobId, token) {
  // EventSource doesn't support custom headers, so we use fetch-based SSE
  const controller = new AbortController();
  let buffer = '';

  const stream = {
    controller,
    onStatus: null,
    onResult: null,
    onError: null,
    // A job parked for the user's choice (status awaiting_pick, or the v2
    // name variants_ready): the server sends its state once as a `concepts`
    // event and closes; a plain `status` event with that status lands here too.
    onVariants: null,
    close() {
      controller.abort();
    },
  };

  (async () => {
    try {
      const res = await fetch(`${API_SERVER}/ai-generator/job/${jobId}/stream`, {
        headers: {
          ...headers(token),
          Accept: 'text/event-stream',
        },
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'SSE connection failed' }));
        stream.onError?.(err);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      // Draining an SSE stream is inherently sequential — the loop and the
      // await inside it are the mechanism, not an oversight.
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line

        let currentEvent = null;
        // eslint-disable-next-line no-restricted-syntax
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));
              const parked = data?.status === 'variants_ready' || data?.status === 'awaiting_pick';
              if (currentEvent === 'status' && parked && stream.onVariants) {
                stream.onVariants(data);
              } else if (currentEvent === 'status') stream.onStatus?.(data);
              else if (currentEvent === 'variants' || currentEvent === 'concepts') stream.onVariants?.(data);
              else if (currentEvent === 'result') stream.onResult?.(data);
              else if (currentEvent === 'error') stream.onError?.(data);
            } catch { /* skip malformed JSON */ }
            currentEvent = null;
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        stream.onError?.({ message: err.message });
      }
    }
  })();

  return stream;
}

export async function renderAiWidget(jobId, mockValues, token) {
  const res = await fetch(`${API_SERVER}/ai-generator/render/${jobId}`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ mockValues }),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export async function getQueueInfo() {
  const res = await fetch(`${API_SERVER}/ai-generator/queue`);
  return res.json();
}

export async function getAiQuota(token) {
  const res = await fetch(`${API_SERVER}/ai-generator/quota`, {
    headers: headers(token),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export async function downloadGeneratedFile(fileUrl, token) {
  const url = `${API_SERVER}${fileUrl.replace('/api/', '/')}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  return blob;
}

/**
 * Download a generated widget together with the name the server gives it:
 * Content-Disposition's filename (the widget's unique WinCC type name), or —
 * when CORS hides that header — the type name read from the file itself.
 * `fileName` is null when neither has one (the caller falls back).
 *
 * @returns {Promise<{blob: Blob, fileName: string|null}>}
 */
export async function downloadGeneratedWidget(fileUrl, token) {
  const url = `${API_SERVER}${fileUrl.replace('/api/', '/')}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  let text = '';
  try { text = await blob.slice(0, 4096).text(); } catch { text = ''; }
  return { blob, fileName: serverWidgetFileName(res.headers.get('Content-Disposition'), text) };
}

/**
 * "Edit my SVGHMI": the user's own .svghmi becomes a completed generator job
 * (pipeline 'import', free) that can then be refined or restyled.
 * POST /ai-generator/import, multipart field `file` (≤ 2 MB) + optional
 * `description` (what the widget shows — Restyle reads it). Errors carry the
 * server's `error` code (LIBRARY_WIDGET, NOT_SVGHMI, FILE_TOO_LARGE, …).
 */
export async function importOwnSvghmi(file, description, token) {
  // NOTE: no Content-Type — the browser adds the multipart boundary.
  const form = new FormData();
  form.append('file', file, file.name);
  if (description) form.append('description', description);
  const res = await fetch(`${API_SERVER}/ai-generator/import`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export async function getActiveJob(token) {
  const res = await fetch(`${API_SERVER}/ai-generator/active`, {
    headers: headers(token),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export async function getMyJobs(token, limit = 20) {
  const res = await fetch(`${API_SERVER}/ai-generator/my-jobs?limit=${limit}`, {
    headers: headers(token),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data.jobs;
}

/**
 * Rate a finished widget: 1 = thumbs up, -1 = thumbs down.
 *
 * Upsert — the user may press the other thumb later and the last verdict wins.
 * `reason` is optional and only meaningful for a thumbs down; a bare thumbs
 * down must stay possible, so nothing here demands one.
 */
export async function rateJob(jobId, rating, token, reason) {
  const res = await fetch(`${API_SERVER}/ai-generator/job/${jobId}/rating`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(reason ? { rating, reason } : { rating }),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

export async function getJobPreview(jobId, token) {
  const res = await fetch(`${API_SERVER}/ai-generator/my-jobs/${jobId}/preview`, {
    headers: headers(token),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

/**
 * Style catalogue for the picker: { styles, default, details, default_detail }.
 * See app/ai-generator/variants.js (buildStyleCatalog) for the shape handling.
 */
export async function getGeneratorStyles(token) {
  const res = await fetch(`${API_SERVER}/ai-generator/styles`, {
    headers: headers(token),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

/**
 * Pick one of a parked job's variants.
 *
 * - status variants_ready (finished variants): POST /job/:id/choose { idx } —
 *   free, answers with the completed job payload (result/rendered_svg).
 * - status awaiting_pick (concept sketches): POST /job/:id/pick { idx } — the
 *   build is queued, so the answer is a queued payload to follow like a
 *   generation.
 */
export async function chooseVariant(jobId, idx, token, status = 'variants_ready') {
  const action = status === 'awaiting_pick' ? 'pick' : 'choose';
  const res = await fetch(`${API_SERVER}/ai-generator/job/${jobId}/${action}`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ idx }),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

/**
 * "Show more variants" for a completed old-path job (free, once per job). The
 * job goes back to generating and ends in variants_ready; 409 MORE_USED when
 * the extra variants were already drawn.
 */
export async function requestMoreVariants(jobId, token) {
  const res = await fetch(`${API_SERVER}/ai-generator/job/${jobId}/more`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

/**
 * Re-render a completed concept-pipeline widget in another style and/or
 * detail level — a new free job (usually already completed in the response).
 */
export async function restyleJob(jobId, style, detail, token) {
  const body = {};
  if (style) body.style = style;
  if (detail) body.detail = detail;
  const res = await fetch(`${API_SERVER}/ai-generator/job/${jobId}/restyle`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

/**
 * Fetch an authenticated image (variant previews, PNG previews) and return an
 * object URL for an <img>. The caller revokes it. Accepts the server's
 * "/api/…" paths (same mapping as downloadGeneratedFile) or absolute URLs.
 */
export async function fetchAuthedImageUrl(url, token) {
  const full = /^https?:\/\//i.test(url) ? url : `${API_SERVER}${url.replace(/^\/api\//, '/')}`;
  const res = await fetch(full, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ── Edit chat ───────────────────────────────────────────────────────────────
// Free-form chat that edits a completed job's SVGHMI in precise steps.
// A message that changes the file costs pricing.generation credits (refunded
// when nothing changed); questions are free. See app/ai-generator/editChatState.js.

/**
 * POST /ai-generator/edit-chat {job_id, message}
 * → 201 {turn_id, job_id, estimate_seconds:{typical, usual_max, max}}.
 * Errors: 402 INSUFFICIENT_CREDITS, 403 PURCHASE_REQUIRED, 409 (a turn is
 * already running), 422 BASE_TOO_LARGE, 404 (endpoint not deployed yet).
 */
export async function startEditChat(jobId, message, token) {
  const res = await fetch(`${API_SERVER}/ai-generator/edit-chat`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ job_id: jobId, message }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

/** GET /ai-generator/edit-chat/:turnId — the turn accumulated so far (poll fallback). */
export async function getEditChatTurn(turnId, token) {
  const res = await fetch(`${API_SERVER}/ai-generator/edit-chat/${encodeURIComponent(turnId)}`, {
    headers: headers(token),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throwApiError(data, res.status);
  return data;
}

/**
 * GET /ai-generator/edit-chat/history?job_id=… → {messages:[…]}.
 * A 404 without an error code means the endpoint is not deployed; the
 * caller then falls back to the one-shot refine box.
 */
export async function getEditChatHistory(jobId, token) {
  const res = await fetch(`${API_SERVER}/ai-generator/edit-chat/history?job_id=${encodeURIComponent(jobId)}`, {
    headers: headers(token),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Request failed');
    err.statusCode = res.status;
    Object.assign(err, data);
    throw err;
  }
  return data;
}

/**
 * GET /ai-generator/edit-chat/:turnId/stream — fetch-based SSE (EventSource
 * cannot send the Authorization header). Handlers: onEvent(name, data) for
 * step / message / revision / done / error, and onBroken(err) when the
 * connection fails or closes before `done`/`error` (the caller then polls).
 */
export function createEditChatSSE(turnId, token) {
  const controller = new AbortController();
  const stream = {
    onEvent: null,
    onBroken: null,
    close() { controller.abort(); },
  };

  (async () => {
    let finished = false;
    try {
      const res = await fetch(`${API_SERVER}/ai-generator/edit-chat/${encodeURIComponent(turnId)}/stream`, {
        headers: { ...headers(token), Accept: 'text/event-stream' },
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        stream.onBroken?.({ statusCode: res.status, message: 'SSE connection failed' });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // Draining an SSE stream is sequential by nature.
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSseBuffer(buffer);
        buffer = parsed.rest;
        // eslint-disable-next-line no-restricted-syntax
        for (const { event, data } of parsed.events) {
          if (event === 'done' || event === 'error') finished = true;
          stream.onEvent?.(event, data);
        }
      }
      if (!finished) stream.onBroken?.({ message: 'Stream closed early' });
    } catch (err) {
      if (err.name !== 'AbortError' && !finished) stream.onBroken?.({ message: err.message });
    }
  })();

  return stream;
}
